import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../../../i18n";
import api from "../../../lib/api";
import { Button, Input } from "../../../components/ui";

const LoginPage = () => {
  const { t } = useTranslation();
  const tf = (key: string, fallback: string) => {
    const value = t(key);
    return value === key ? fallback : value;
  };
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const emailInputRef = useRef<HTMLInputElement | null>(null);
  const { login } = useAuth();

  useEffect(() => {
    if (emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (location.state?.fromRegister && emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, [location.state]);

  const handleLogin = async () => {
    if (!email || !password) return;
    setIsLoading(true);
    setErrorMessage("");
    try {
      const { data } = await api.post("/login", { email, password });
      await login({
        email: data.email || email,
        userId: data.userId,
        isAdmin: data.isAdmin,
        redirectTo: location.state?.fromRegister ? "/onboarding" : undefined,
      });
    } catch (error) {
      setErrorMessage(t("auth.login.errorInvalid"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleLogin();
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex flex-col lg:flex-row bg-surface-nav text-on-surface">
      {/* Linkes Produkt-Panel (Design 7b) */}
      <section
        className="hidden lg:flex lg:w-[46%] shrink-0 flex-col bg-surface-container-low border-r border-outline-variant px-12 py-11"
        style={{
          backgroundImage:
            "radial-gradient(700px 500px at 20% 10%, rgb(var(--primary) / 0.1), transparent 60%)",
        }}
      >
        <div className="flex items-center gap-2.5">
          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
            <circle
              cx="12"
              cy="12"
              r="10"
              fill="none"
              stroke="rgb(var(--primary))"
              strokeWidth="2"
            />
            <circle cx="12" cy="12" r="4" fill="rgb(var(--primary))" />
          </svg>
          <span className="text-[15px] font-semibold tracking-[.01em]">Customy</span>
        </div>
        <h1 className="text-[30px] leading-[1.25] font-semibold tracking-[-.02em] mt-14 max-w-[400px]">
          {tf("auth.login.panelTitle", "Das Band lief weiter, während du weg warst.")}
        </h1>
        <p className="text-[13.5px] leading-relaxed text-on-surface-muted mt-3.5 max-w-[380px]">
          {tf(
            "auth.login.panelSubtitle",
            "Melde dich an, um Review-Warteschlange, laufende Jobs und Downloads zu sehen.",
          )}
        </p>

        {/* Job-Vignette (statisches DIV-Mockup) */}
        <div
          className="mt-10 border border-outline-variant bg-surface-container rounded-xl p-4 max-w-[420px] shadow-ambient"
          aria-hidden="true"
        >
          <div className="flex items-center gap-2">
            <span className="w-[7px] h-[7px] rounded-full bg-success" />
            <span className="text-[12.5px] font-medium">
              {tf("auth.login.vignetteBatch", "Mockup-Batch läuft")}
            </span>
            <span className="ml-auto font-mono text-[10.5px] text-on-surface-muted">170/250</span>
          </div>
          <div className="h-1 rounded-full bg-on-surface/[.08] overflow-hidden mt-2.5">
            <div className="w-[68%] h-full bg-success rounded-full" />
          </div>
          <div className="flex items-center gap-2 mt-3.5">
            <span className="w-[7px] h-[7px] rounded-full bg-warning" />
            <span className="text-[12.5px] font-medium text-on-surface-variant">
              {tf("auth.login.vignetteReview", "96 Designs warten auf Review")}
            </span>
            <span className="ml-auto text-[11px] font-medium text-primary">
              {tf("auth.login.vignetteOpen", "öffnen →")}
            </span>
          </div>
        </div>
      </section>

      {/* Rechtes Formular */}
      <section className="flex-1 flex items-start lg:items-center justify-center px-6 py-12 lg:py-16">
        <div className="w-full max-w-[380px]">
          <h2 className="text-2xl font-semibold tracking-[-.015em] text-on-surface">
            {t("auth.login.tabLogin")}
          </h2>
          <p className="text-[13px] text-on-surface-muted mt-1.5">
            {tf("auth.login.newHere", "Neu hier?")}{" "}
            <Link
              to="/register"
              className="text-primary font-medium hover:text-primary-fixed transition-colors duration-150"
            >
              {tf("auth.login.newHereCta", "Konto erstellen")}
            </Link>
          </p>

          {/* Fehler */}
          {errorMessage && (
            <div
              className="flex items-center gap-3 p-3.5 mt-6 rounded-lg bg-error/10 border border-error/20"
              role="alert"
            >
              <span className="material-symbols-outlined text-error text-xl leading-none">
                error
              </span>
              <p className="text-[13px] text-error">{errorMessage}</p>
            </div>
          )}

          {/* Formular */}
          <form
            className="mt-7 space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              handleLogin();
            }}
          >
            <Input
              id="login-email"
              ref={emailInputRef}
              type="email"
              name="email"
              autoComplete="email"
              spellCheck={false}
              required
              label={t("auth.common.emailLabel")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t("auth.common.emailPlaceholder")}
            />

            <div className="space-y-2">
              <div className="flex items-center">
                <label
                  htmlFor="login-password"
                  className="font-sans text-xs font-medium text-on-surface-variant"
                >
                  {t("auth.common.passwordLabel")}
                </label>
                <Link
                  to="/password-reset"
                  className="ml-auto text-xs font-medium text-primary hover:text-primary-fixed transition-colors duration-150"
                >
                  {tf("auth.login.forgotShort", "Vergessen?")}
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={t("auth.common.passwordPlaceholder")}
                  style={{ paddingRight: "2.75rem" }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  aria-label={
                    showPassword ? t("auth.common.hidePassword") : t("auth.common.showPassword")
                  }
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-on-surface-faint hover:text-on-surface transition-colors duration-150"
                >
                  <span className="material-symbols-outlined text-lg leading-none">
                    {showPassword ? "visibility_off" : "visibility"}
                  </span>
                </button>
              </div>
            </div>

            <Button type="submit" size="lg" fullWidth disabled={isLoading} className="!mt-6">
              {isLoading ? t("auth.login.submitInProgress") : t("auth.login.submit")}
            </Button>
          </form>

          {/* Sitzungs-Hinweis */}
          <div className="flex items-start gap-2 mt-6 text-[11.5px] leading-relaxed text-on-surface-faint">
            <span className="material-symbols-outlined text-sm leading-none mt-0.5">lock</span>
            {tf(
              "auth.login.sessionNote",
              "Sitzung bleibt 30 Tage aktiv. Abmelden jederzeit über das Nutzermenü.",
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default LoginPage;
